<template>
  <div class="initial-setup">
    <SidebarLayout2 :breadcrumb="breadcrumb" :pageTitle="pageTitle">
      <!-- BARRA LATERAL -->
      <template #sidebar>
        <!-- DICAS -->
        <SidebarBlock class="pmw-page-actions">
          <Heading class="side-bar-block">Ações da página</Heading>
          <Button
            :disabled="isDisabledSave"
            tooltipText="Preencha os itens com o *(asterisco) que indicam os campos obrigatórios para salvar."
            @click="saveConfiguration"
            >Salvar
          </Button>

          <Heading class="sidebar--tip-title">
            Dica
          </Heading>
          <Paragraph class="sidebar--tip-text">
            <span class="sidebar--tip-alert">Atenção:</span>
            As alterações efetuadas nessa tela podem demorar até 24 horas para
            refletir no sistema.
          </Paragraph>
        </SidebarBlock>
      </template>

      <!-- CONTEÚDO -->
      <template #content>
        <!-- TITULO -->
        <b-row class="application-colors">
          <b-col>
            <Heading element="h3" level="3">
              Cores da aplicação
            </Heading>
          </b-col>
        </b-row>
        <!-- SUB-TITULO -->
        <b-row>
          <b-col>
            <Paragraph fontSize="small"
              >Selecione as cores na página de simulação, copie os códigos e
              cole abaixo.</Paragraph
            >
          </b-col>
        </b-row>
        <!-- BOTÃO SELECIONAR CORES -->
        <b-row class="generic-spaces">
          <b-col cols="2">
            <Button
              :hasIcon="true"
              :link="colorTestUrl"
              layout="outline"
              element="a"
              target="_blank"
              >Selecionar cores</Button
            >
          </b-col>
        </b-row>
        <b-row class="row-spaces--input-code-color">
          <b-col cols="6">
            <FormInput
              :value="clipboardColorsInputValue"
              label="Código das cores"
              placeholder="Cole aqui as cores (ctrl+v)"
              @input="pasteClipboardTextEvent"
            />
          </b-col>
        </b-row>
        <!-- INPUTS DE CORES -->
        <b-row v-if="configData.cores" class="initial-setup-color-grid">
          <ColorField
            v-for="(color, key) in colorsFieldsLabels"
            :key="`color-${key}`"
            :label="colorsFieldsLabels[key]"
            :colorCode="key"
            :colorsObj="configData.cores"
          />
        </b-row>

        <!-- LINHA 2 -->
        <SeparateLine :left="0" :right="0" />

        <!-- INPUTS -->
        <b-row>
          <b-col>
            <!-- TITULO -->
            <Heading element="h3" level="3">Dados</Heading>
          </b-col>
        </b-row>
        <b-row class="generic-spaces">
          <b-col cols="4">
            <label class="input-label">GTM</label>
            <div class="input">
              <input
                v-model.trim="fieldGtm"
                :disabled="gtmToSend.length >= 8"
                placeholder="GTM"
                cols="4"
                class="input--field"
                maxlength="15"
                @keypress.enter.space="addGtm"
                @blur="addGtm"
              />
            </div>

            <DataTable
              :cols="gtmDatatable.columns"
              :rows="gtmDatatable.rows"
              :colsMinWidths="gtmDatatable.colsMinWidths"
              :isLoading="false"
              :sortable="false"
              class="gtm-datatable"
            >
              <template v-slot:gtm="slotProps">
                <div class="gtm-datatable--gtm-slot">
                  <PmwParagraph
                    size="paragraph-40"
                    class="gtm-datatable--gtm-text"
                  >
                    {{ slotProps.rowDataObj.gtm }}
                  </PmwParagraph>
                  <span @click.stop.prevent="removeGtm(slotProps.rowDataObj)">
                    <Icon icon="x-icon" class="gtm-datatable--action" />
                  </span>
                </div>
              </template>

              <template v-slot:actions="slotProps">
                <div class="gtm-datatable--gtm-action">
                  <PmwInputCheckbox
                    v-model="slotProps.rowDataObj.actions.cash"
                    :checked="slotProps.rowDataObj.actions.cash === true"
                    :disabled="
                      !slotProps.rowDataObj.actions.cash && countCash >= 4
                    "
                  />
                  <PmwParagraph size="paragraph-40" class="gtm-text">
                    Dinheiro
                  </PmwParagraph>
                </div>
                <div class="gtm-datatable--gtm-action">
                  <PmwInputCheckbox
                    v-model="slotProps.rowDataObj.actions.points"
                    :checked="slotProps.rowDataObj.actions.points === true"
                    :disabled="
                      !slotProps.rowDataObj.actions.points && countPoints >= 4
                    "
                  />
                  <PmwParagraph size="paragraph-40" class="gtm-text">
                    Pontos
                  </PmwParagraph>
                </div>
              </template>
            </DataTable>
          </b-col>
          <b-col cols="2">
            <Button
              :hasIcon="true"
              :disabled="gtmToSend.length >= 8 || fieldGtm === ''"
              :tooltipText="tooltipMenErro"
              layout="outline"
              class="form-input-button"
              @click="addGtm"
            >
              Adicionar
            </Button>
          </b-col>
        </b-row>
        <b-row class="generic-spaces--email-input">
          <b-col cols="3">
            <FormInput
              v-model="configData.emailToken"
              label="E-mail from para token"
              placeholder="Ex: Resumo diario"
              required
            />
          </b-col>
          <b-col cols="4">
            <label class="input-label"
              >E-mails para o envio de resumo diário de reservas</label
            >
            <div class="input">
              <input
                v-model="fieldEmailsResumoDiarioReservas"
                placeholder="E-mails"
                cols="4"
                class="input--field"
                type="email"
                @keypress.enter.space="addEmail($event)"
                @blur="addEmail($event)"
              />
            </div>

            <Chip
              v-for="(email, i) in emailsToSend"
              :key="`email-chip-${i}`"
              class="email-chip"
              cols="8"
              close
              @close="removeEmail(i)"
            >
              {{ email }}
            </Chip>
          </b-col>
        </b-row>

        <!-- LINHA DIVISÓRIA -->
        <SeparateLine :left="0" :right="0" />

        <!-- TITULO DE IMAGENS -->
        <b-row class="generic-spaces--image">
          <b-col>
            <Heading element="h3" level="3">
              Cadastro de imagens
            </Heading>
          </b-col>
        </b-row>

        <!-- CAMPOS DE UPLOAD DE IMAGEM -->
        <div class="pmw-home">
          <!-- UPLOAD LOGO -->
          <div class="pmw-input-file">
            <PmwImageUpload
              v-model="imgLogo"
              :maxSize="512"
              :imageWidth="160"
              :imageHeight="60"
              :inputWidth="350"
              inputTitle="Logo da aplicação"
              infoText="Aparece no cabeçalho do motor e no cabeçalho do e-mail de reserva."
              tooltipMinWidth="210"
              minSizeErrorText="A imagem precisa ter no mínimo a dimensão de 160x60 pixels. Tente novamente com uma imagem maior."
              sizeErrorText="A imagem selecionada excede o limite de tamanho. Tente novamente com uma imagem menor que 500KB"
              sizeInfo="Dimensão da imagem 160 x 60 pixels"
              inputDescription="São aceitos arquivos .JPG e .PNG de até 500KB."
              accept=".png, .jpg"
              inputTextBox="Arraste a imagem para cá"
              class="pmw-input-file--logo"
              required
              infoIcon
            />
          </div>

          <!-- UPLOAD DE FAVICON -->
          <div class="pmw-input-file">
            <PmwImageUpload
              v-model="imgFavicon"
              :maxSize="512"
              :imageWidth="32"
              :imageHeight="32"
              :inputWidth="375"
              inputTitle="Favicon"
              infoText="Aparece na guia do navegador ao lado do nome da página."
              tooltipMinWidth="200"
              minSizeErrorText="A imagem precisa ter no mínimo a dimensão de 32x32 pixels. Tente novamente com uma imagem maior."
              sizeErrorText="A imagem selecionada excede o limite de tamanho. Tente novamente com uma imagem menor que 500KB"
              sizeInfo="Dimensão da imagem 32 x 32 pixels"
              inputDescription="São aceitos arquivos .JPG e .PNG de até 500KB."
              accept=".png, .jpg"
              inputTextBox="Arraste a imagem para cá"
              class="pmw-input-file--favicon"
              required
              infoIcon
            />
          </div>

          <!-- UPLOAD DE OPENGRAPH -->
          <div class="pmw-input-file">
            <PmwImageUpload
              v-model="imgOpenGraph"
              :maxSize="512"
              :imageWidth="200"
              :imageHeight="200"
              :inputWidth="375"
              inputTitle="OpenGraph"
              infoText="Aparece quando o link da página é compartilhado."
              tooltipMinWidth="200"
              minSizeErrorText="A imagem precisa ter no mínimo a dimensão de 200x200 pixels. Tente novamente com uma imagem maior."
              sizeErrorText="A imagem selecionada excede o limite de tamanho. Tente novamente com uma imagem menor que 500KB"
              sizeInfo="Dimensão da imagem 200 x 200 pixels"
              inputDescription="São aceitos arquivos .JPG e .PNG de até 500KB."
              accept=".png, .jpg"
              inputTextBox="Arraste a imagem para cá"
              class="pmw-input-file--opengraph"
              infoIcon
            />
          </div>
        </div>

        <!-- UPLOAD DE HEADER -->
        <div class="pmw-home--header">
          <div class="pmw-input-file">
            <PmwImageUpload
              v-model="imgHeader"
              :maxSize="512"
              :imageWidth="920"
              :imageHeight="94"
              :inputWidth="375"
              inputTitle="Header e-mail diário"
              infoText="Logo do resumo diário de email."
              tooltipMinWidth="195"
              minSizeErrorText="A imagem precisa ter no mínimo a dimensão de 920x94 pixels. Tente novamente com uma imagem maior."
              sizeErrorText="A imagem selecionada excede o limite de tamanho. Tente novamente com uma imagem menor que 500KB"
              sizeInfo="Dimensão da imagem 920 x 94 pixels"
              inputDescription="São aceitos arquivos .JPG e .PNG de até 500KB."
              accept=".png, .jpg"
              inputTextBox="Arraste a imagem para cá"
              class="pmw-input-file--header"
              required
              infoIcon
            />
          </div>
        </div>

        <!-- UPLOAD BANNER SECUNDÁRIO -->
        <div class="pmw-home--header">
          <div class="pmw-input-file">
            <PmwImageUpload
              v-model="imgBanner"
              :maxSize="512"
              :imageWidth="600"
              :imageHeight="250"
              :inputWidth="375"
              inputTitle="Banner Secundário"
              minSizeErrorText="A imagem precisa ter no mínimo a dimensão de 600x250 pixels. Tente novamente com uma imagem maior."
              sizeErrorText="A imagem selecionada excede o limite de tamanho. Tente novamente com uma imagem menor que 500KB"
              sizeInfo="Dimensão da imagem 600 x 250 pixels"
              inputDescription="São aceitos arquivos .JPG e .PNG de até 500KB."
              accept=".png, .jpg"
              inputTextBox="Arraste a imagem para cá"
              class="pmw-input-file--header"
            />
          </div>

          <PmwFormInput
            v-model="urlBanner"
            :disabled="!imgBanner"
            :error="!urlBannerIsValid()"
            :showErrorFeedbackMessage="true"
            :infoIcon="true"
            :infoText="'Exemplo: https://www.exemplo.com.br'"
            :feedbackMessage="'Url inválido'"
            tooltipMinWidth="250"
            label="URL da ação do banner"
            placeholder="Digite um texto de descrição"
            class="banner-action"
          />
        </div>

        <!-- LINHA DIVISÓRIA -->
        <SeparateLine :left="0" :right="0" />

        <!-- TARIFA LOGADA -->
        <div class="logged-rate">
          <Heading element="h3" level="3" class="logged-rate--title">
            Tarifa Logada
          </Heading>

          <Paragraph class="logged-rate--description">
            A Tarifa Logada é um incentivo do hotel para desbloquear benefícios
            exclusivos aos usuários que realizam login ou cadastro. Configure
            esses incentivos diretamente na tela de
            <router-link
              class="logged-rate--link"
              to="/desconto-exclusivo"
              target="_blank"
              >Desconto Exclusivo</router-link
            >. Ao ativar esta funcionalidade, uma nova área de login será
            apresentada aos usuários, com mensagens que os incentivam a se
            cadastrar e fazer login.
          </Paragraph>

          <div class="logged-rate--activate-incentive">
            <PmwInputCheckbox
              v-model="activeLoggedRate"
              :checked="activeLoggedRate === true"
              label="Ativar incentivo de Login e Cadastro na área publica."
            />
          </div>

          <div v-if="activeLoggedRate" class="logged-rate--options">
            <Paragraph fontWeight="medium" class="logged-rate--options-label">
              Escolha uma opção para utilizar como mensagem de incentivo:<span
                class="logged-rate--options-label__alert"
                >*</span
              >
            </Paragraph>

            <div class="logged-rate--options-grid">
              <!-- MENSAGEM PADRÃO -->
              <div class="logged-rate-option">
                <PmwInputRadio
                  v-model="typeLoggedRate"
                  :checked="typeLoggedRate === '1'"
                  inputValue="1"
                  label="Mensagem padrão"
                  name="typeLoggedRate"
                  class="logged-rate-option--radio"
                />

                <img
                  class="logged-rate-option--image"
                  src="@/assets/images/public-area/modal-login-default.svg"
                />
              </div>

              <!-- MENSAGEM DE BENEFÍCIO -->
              <div class="logged-rate-option">
                <PmwInputRadio
                  v-model="typeLoggedRate"
                  :checked="typeLoggedRate === '2'"
                  inputValue="2"
                  label="Mensagem de benefício"
                  name="typeLoggedRate"
                  class="logged-rate-option--radio"
                />

                <img
                  class="logged-rate-option--image"
                  src="@/assets/images/public-area/modal-login-benefits.svg"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- LINHA DIVISÓRIA -->
        <SeparateLine :left="0" :right="0" />

        <!-- TITULO DA POLITICA -->
        <b-row class="generic-spaces__small">
          <b-col>
            <Heading element="h3" level="3">
              Política de Privacidade
            </Heading>
          </b-col>
        </b-row>

        <b-row>
          <Textarea
            :value="privacyDescription"
            :counter="true"
            :maxlength="8000"
            :hasLanguages="true"
            :language="language"
            :languageCodes="{ pt: 'pt-BR', en: 'en-US', es: 'es-ES' }"
            :row="7"
            label="Descrição"
            placeholder="Digite uma descrição."
            class="text-area"
            @changeLanguage="language = $event"
            @input="updatePrivacyDescription"
          />
        </b-row>

        <!-- LINHA DIVISÓRIA -->
        <SeparateLine :left="0" :right="0" />

        <!-- TITULO TABELA -->
        <b-row class="generic-spaces">
          <b-col>
            <Heading element="h3" level="3">
              Cadastro de usuários
            </Heading>
          </b-col>
        </b-row>

        <!-- TABELA -->
        <b-row>
          <b-col>
            <div class="filters">
              <SearchInput
                v-model="searchTerm"
                class="filters--search_width"
                @keypress.enter="searchTermSelected = $event.target.value"
                @selectItem="searchSelectedItemHandler"
              />
            </div>
            <DataTable
              :cols="gridColumns"
              :rows="gridData"
              :filterKey="filterKey"
              :colsMinWidths="colsWidths"
              :noFrames="true"
              :sortable="true"
              :searchableColumns="['login', 'senha', 'ativo']"
              :isLoading="isLoadingTableContent"
              class="datatable"
              ref="datatable"
              notFoundMessage="Não há usuários correspondentes com a busca."
            >
              <template v-slot:acoes="slotProps">
                <button
                  :id="slotProps.rowDataObj.codigo"
                  class="datatable--action"
                  @click.stop.prevent="openModalUser(slotProps.rowDataObj)"
                >
                  <Paragraph fontSize="small">
                    Editar
                  </Paragraph>
                </button>
                <button
                  :id="slotProps.rowDataObj.codigo"
                  class="datatable--action__delete"
                  @click="openDeleteUserModal(slotProps.rowDataObj)"
                >
                  <Paragraph fontSize="small">
                    Excluir
                  </Paragraph>
                </button>
              </template>
            </DataTable>
          </b-col>
        </b-row>

        <!-- BOTOES DE MODAL -->
        <b-row class="last-buttons">
          <b-col cols="2">
            <Button :hasIcon="true" layout="outline" @click="createNewUser"
              >Adicionar usuário</Button
            >
          </b-col>
        </b-row>
      </template>
    </SidebarLayout2>

    <!-- MODAL TABLE -->
    <Modal
      v-model="modalTable"
      title="Usuário"
      confirmButtonText="Salvar"
      @modal-confirm="updateUser"
    >
      <template slot="body-content">
        <div class="col-12">
          <div class="row">
            <div class="col modal-spaces">
              <FormInput
                v-model="modalLogin"
                :readonly="true"
                :clearButtonText="''"
                label="Login"
              />
            </div>
          </div>
          <div class="row">
            <div class="col modal-spaces">
              <FormInput
                v-model="modalPassword"
                :readonly="true"
                :clearButtonText="''"
                label="Senha"
              />
            </div>
          </div>
          <div class="row">
            <div class="col modal-spaces modal-spaces__description">
              <FormInput
                v-model="modalDescription"
                :maxlength="50"
                label="Descrição"
                placeholder="Insira uma descrição"
                showCounter
              />
            </div>
          </div>
        </div>
        <div class="row modal-spaces">
          <div class="col-6">
            <Heading element="h3" level="2">
              Hotéis
            </Heading>
            <SearchInput
              v-model="searchHotel"
              class="modal-spaces--inputs-margin"
              @keypress.enter="searchTermSelected = $event.target.value"
              @selectItem="searchSelectedItemHandler"
            />
            <FilterDropdownCheckbox
              v-model="selectedHotels"
              :options="hotelsOptions"
              :searchTerm="searchHotel"
              @input="$emit('input', $event)"
            />
          </div>
          <div class="col-6">
            <Heading element="h3" level="2">
              Métodos
            </Heading>
            <SearchInput
              v-model="searchMethods"
              class="modal-spaces--inputs-margin"
              @keypress.enter="searchTermSelected = $event.target.value"
              @selectItem="searchSelectedItemHandler"
            />
            <FilterDropdownCheckbox
              v-model="selectedMethods"
              :options="methodsOptions"
              :searchTerm="searchMethods"
              @input="$emit('input', $event)"
            />
          </div>
        </div>
      </template>
    </Modal>

    <PmwAlertModal
      v-model="showDeleteUserModal"
      cancelButtonColor="blue-100"
      confirmButtonText="Sim, deletar usuário"
      confirmButtonColor="blue-100"
      title="Você irá excluir um usuário"
      @alert-modal-confirm="deleteUser"
    />

    <AlertChip v-model="showAlertChip">
      {{ alertChipMessage }}
    </AlertChip>
    <AlertChip v-model="showAlertChipUpdateUser">
      {{ alertChipMessageUpdateUser }}
    </AlertChip>
    <AlertChip v-model="showAlertChipCreateConfig">
      {{ alertChipCreateConfig }}
    </AlertChip>

    <ClipboardAlert
      v-model="showClipboardAlert"
      @alert-modal-confirm="useClipboardColors"
      @alert-modal-cancel="showClipboardAlert = false"
    />
  </div>
</template>
<script>
import SidebarLayout2 from '@/layouts/SidebarLayout2'
import Heading from '@/components/_base/elements/Heading'
import Button from '@/components/_base/elements/Button'
import FormInput from '@solucoes/components-lib-tmp/src/components/elements/FormInput'
import AlertChip from '@/components/_base/elements/AlertChip'
import SearchInput from '@/components/_base/elements/SearchInput'
import Paragraph from '@/components/_base/elements/Paragraph'
import SidebarBlock from '@/components/_base/patterns/SidebarBlock'
import DataTable from '@solucoes/components-lib-tmp/src/components/patterns/DataTable'
import Modal from '@/components/_base/patterns/Modal'
import SeparateLine from '@/components/_base/patterns/SeparateLine'
import Chip from '@/components/_base/patterns/Chip'
import FilterDropdownCheckbox from '@/components/_base/patterns/FilterDropdownCheckbox'
import PmwImageUpload from '@solucoes/components-lib-tmp/src/components/patterns/PmwImageUpload'
import Textarea from '@solucoes/components-lib-tmp/src/components/elements/Textarea'
import ColorField from '@/components/setup/initial/ColorField'
import ClipboardAlert from '@/components/setup/initial/ClipboardAlert'
import PmwAlertModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwAlertModal'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import Icon from '@solucoes/components-lib-tmp/src/components/elements/Icon'
import PmwInputCheckbox from '@solucoes/components-lib-tmp/src/components/elements/PmwInputCheckbox'
import PmwInputRadio from '@solucoes/components-lib-tmp/src/components/elements/PmwInputRadio'
import PmwFormInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormInput'

import { validateEmail } from '@/helpers/string'

export default {
  name: 'InitialSetup',

  components: {
    SidebarLayout2,
    Heading,
    Button,
    FormInput,
    AlertChip,
    SearchInput,
    Paragraph,
    SidebarBlock,
    DataTable,
    Modal,
    SeparateLine,
    Chip,
    FilterDropdownCheckbox,
    PmwImageUpload,
    Textarea,
    ColorField,
    ClipboardAlert,
    PmwAlertModal,
    PmwParagraph,
    Icon,
    PmwInputCheckbox,
    PmwInputRadio,
    PmwFormInput
  },

  data() {
    return {
      language: 'pt-BR' /** Linguagem selecionada */,
      imgLogo: null /** Imagem do Logo da aplicação*/,
      imgFavicon: null /** Imagem do Favicon*/,
      imgHeader: null /** Imagem do Header do e-mail*/,
      imgOpenGraph: null /** Imagem do OpenGraph*/,
      imgBanner: null /** Imagem do banner secundário*/,
      inputs: 1,
      showAlertChip: false,
      showAlertChipUpdateUser: false,
      showAlertChipCreateConfig: false,
      alertChipMessage: 'Usuário adicionado com sucesso',
      alertChipMessageUpdateUser: 'Usuário atualizado com sucesso',
      alertChipCreateConfig: 'Configuração salva com sucesso',
      isLoadingTableContent: true,
      pageTitle: 'Configuração inicial',
      searchTerm: '',
      searchHotel: '',
      searchMethods: '',
      searchTermSelected: '',
      gtmDatatable: {
        columns: [
          {
            alias: 'GTM',
            name: 'gtm',
            sortable: false
          },
          {
            alias: 'Ambiente de pagamento',
            name: 'actions',
            sortable: false
          }
        ],
        rows: [],

        colsMinWidths: {
          gtm: '140px',
          actions: '200px'
        }
      },

      breadcrumb: [
        {
          label: 'Configurações'
        },
        {
          label: 'Configuração Inicial',
          url: '/v2/configuracao-inicial/Index',
          isSpa: true /** Se deve ser um link para o site novo ou antigo */
        }
      ],
      fieldEmailsResumoDiarioReservas: '',
      fieldGtm: '',
      configData: {
        emailsResumoDiarioReservas: [],
        gtm: [],
        emailToken: '',
        cores: {
          c1: '',
          c2: '',
          c3: '',
          c4: '',
          c5: '',
          c6: '',
          c7: '',
          c8: '',
          c9: '',
          c10: '',
          c11: '',
          c12: '',
          corEmail: '',
          corFundoEmail: ''
        }
      },
      clipboardColors: '' /** Cores vindas do clipboard */,
      clipboardColorsInputValue: '' /** Cores vindas do clipboard */,
      gridData: [],
      colsWidths: {
        cupom: '50%'
      } /** Objeto com os tamanhos mínimos de cada coluna */,
      gridColumns: [
        /** Colunas do Tabela */
        { name: 'login', alias: 'Login' },
        { name: 'senha', alias: 'Senha' },
        { name: 'acoes', alias: '' }
      ],
      modalTable: false,
      selectedHotels: [],
      selectedMethods: [],
      arrayMethods: [],
      arrayHotels: [],
      arrayUsers: [],
      modalLogin: '',
      modalPassword: '',
      methodsOptions: [],
      hotelsOptions: [],
      userData: null,
      description: [],
      /** Cores que poderão ser editadas */
      colorsFieldsLabels: {
        c1: 'Background do header',
        c2: 'Texto do header (itens no menu)',
        c5: 'Background da busca',
        c6: 'Background da área "Minhas reservas"',
        c7: 'Cor dos campos da busca',
        c8: 'Hover dos campos da busca',
        c9: 'Cor do botão “Modifique sua busca”',
        c11: 'Botão buscar e links clicáveis',
        c12: 'Hover do botão buscar e links clicáveis',
        corEmail: 'Background dos novos e-mails',
        corFundoEmail: 'Background dos antigos e-mails'
      },

      showClipboardAlert: false,

      modalDescription: '',

      showDeleteUserModal: false,

      isDisabledSave: false,

      urlBanner: '',

      activeLoggedRate: false,

      typeLoggedRate: null
    }
  },

  computed: {
    /** Conta quantos checkbox de dinheiro estão marcados */
    countCash() {
      return this.gtmDatatable.rows.filter(
        (row) => row.data?.actions?.cash === true
      ).length
    },

    /** Conta quantos checkbox de pontos estão marcados */
    countPoints() {
      return this.gtmDatatable.rows.filter(
        (row) => row.data?.actions?.points === true
      ).length
    },

    hasValidActions() {
      return (
        this.gtmDatatable.rows.length === 0 ||
        this.gtmDatatable.rows.some((item) => {
          const actions = item.data?.actions || {}
          return actions.cash === true || actions.points === true
        })
      )
    },

    /** Descrição do politica de privacidade */
    privacyDescription() {
      return (
        this.description.find((item) => item.idioma.includes(this.language))
          ?.descricao || []
      )
    },

    disabledSave() {
      /** Habilitação do botão de salvar */
      if (
        !validateEmail(this.configData.emailToken) ||
        this.imgLogo === null ||
        this.imgLogo === '' ||
        this.imgFavicon === null ||
        this.imgFavicon === '' ||
        this.imgHeader === null ||
        this.imgHeader === ''
      ) {
        return true
      }
      return false
    },

    /** Filtra os campos de busca */
    filterKey() {
      if (this.searchTerm.trim() !== '') return this.searchTermSelected
      if (this.searchHotel.trim() !== '') return this.searchTermSelected
      if (this.searchMethods.trim() !== '') return this.searchTermSelected

      return ''
    },

    emailsToSend() {
      return this.configData.emailsResumoDiarioReservas
    },

    gtmToSend() {
      return this.configData.gtm
    },

    tooltipMenErro() {
      let message = ''
      if (this.fieldGtm === '' && this.gtmToSend.length < 4) {
        message = 'Preencha o campo para adicionar um GTM.'
      } else if (this.gtmToSend.length >= 4) {
        message =
          'Não é possível realizar a inclusão de mais um GTM. O limite para inclusão é 8.'
      }
      return message
    },

    /** Retorna o link para a tela de teste de cores */
    colorTestUrl() {
      return process.env.VUE_APP_COLOR_TESTER_URL
    }
  },

  watch: {
    gtmDatatable: {
      handler() {
        this.updateDisabledSave()
      },
      deep: true
    },

    urlBanner: {
      handler() {
        this.updateDisabledSave()
      },
      deep: true
    },

    searchTerm(newValue) {
      if (newValue === '') this.searchTermSelected = newValue
    },

    searchHotel(newValue) {
      if (newValue === '') this.searchTermSelected = newValue
    },

    searchMethods(newValue) {
      if (newValue === '') this.searchTermSelected = newValue
    },

    fieldGtm(newValue) {
      this.fieldGtm = newValue.toUpperCase()
    }
  },

  created() {
    this.getMethods()
    this.getHotels()
    this.getUsers()
    this.getConfig()
    this.getPrivacyPolicy()
    this.getClipboarContent()
  },

  mounted() {
    document.addEventListener('visibilitychange', this.getClipboarContent)
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.getClipboarContent)
  },

  methods: {
    urlBannerIsValid() {
      const urlPattern = /^(https?:\/\/)[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i
      return (
        this.urlBanner === '' ||
        (this.urlBanner.length > 2 && urlPattern.test(this.urlBanner))
      )
    },

    updateDisabledSave() {
      this.isDisabledSave =
        !this.configData.emailToken.length ||
        !this.imgLogo ||
        !this.imgFavicon ||
        !this.imgHeader ||
        !this.hasValidActions ||
        !this.urlBannerIsValid()
    },

    /** Atualizar o campo de textarea da descrição da politica de privacidade
     * @param {String} value: valor do textarea
     */
    updatePrivacyDescription(value) {
      const foundedItem = this.description.find((item) =>
        item.idioma.includes(this.language)
      )
      if (foundedItem) {
        foundedItem.descricao = value
      }

      this.$emit('changeDescription', this.description)
    },

    /* Retorna os valores da politica de privacidade cadastradas */
    getPrivacyPolicy() {
      this.$initialSetting.getPolicy().then((response) => {
        this.description = response.data
      })
    },

    /** Tenta pegar o conteúdo da área de transferência */
    getClipboarContent() {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          navigator.clipboard.readText().then((clipText) => {
            const colorParams = this.getColorsParamsFromClipboard(clipText)

            if (
              this.clipboardColors &&
              this.isClipboarEqualToColorParams(colorParams)
            )
              return

            if (colorParams) {
              this.showClipboardAlert = true
              this.clipboardColors = colorParams
            }
          })
        }, 1000)
      }
    },

    /**
     * Verifica se o conteúdo do clipboard
     * já não está sendo utilizado
     *
     * @param {string[]} clipColorsArr array com as cores do link do clipboard
     */
    isClipboarEqualToColorParams(clipColorsArr) {
      if (!clipColorsArr) return false

      const differentColors = clipColorsArr.filter(
        (item) =>
          this.clipboardColors.findIndex((arrItem) => arrItem === item) < 0
      )

      if (
        clipColorsArr.length === this.clipboardColors.length &&
        !differentColors.length
      )
        return true

      return false
    },

    /**
     * Extrai os parametros de cores do
     * conteúdo da área de transferência
     *
     * @param {string} clipText texto da área de transferência
     */
    getColorsParamsFromClipboard(clipText) {
      if (!clipText) return false

      const colorArr = [
        ...clipText.matchAll(/(c\d{1,2}=[0-9a-fA-F]{6})/gm)
      ].flatMap((color) => color[0])

      if (colorArr.length) {
        return colorArr
      }

      return false
    },

    /**
     * Usa as cores contidas na área de transferência
     * para popular os inputs
     */
    useClipboardColors() {
      this.clipboardColors.forEach((color) => {
        const colorEntry = color.split('=')
        this.configData.cores[colorEntry[0]] = `#${colorEntry[1]}`
      })

      this.showClipboardAlert = false
    },

    /**
     * Gerencia o conteúdo inserido no
     * input para colar o link com as cores
     *
     * @param {string} $event link copiado da tela de teste de cores
     */
    pasteClipboardTextEvent($event) {
      const params = this.getColorsParamsFromClipboard($event)

      if (params) {
        this.clipboardColors = params

        this.clipboardColorsInputValue = params
          .map((param) => `#${param.split('=')[1]}`)
          .join(' ')

        this.useClipboardColors()
      } else {
        this.clipboardColorsInputValue = ''
      }
    },

    /**
     * Atualiza ou adiciona um novo GTM na tabela de dados
     *
     * @param {string} gtm Identificador do GTM a ser adicionado ou atualizado
     * @param {string} actionType Tipo de ação associada ao GTM
     */

    updateOrAddGtm(gtm, actionType) {
      const existingRow = this.gtmDatatable.rows.find(
        (row) => row.data.gtm === gtm
      )

      if (existingRow) {
        existingRow.data.actions[actionType] = true
      } else {
        this.configData.gtm.push(gtm)
        this.gtmDatatable.rows.push({
          data: { gtm, actions: { [actionType]: true } }
        })
      }
    },

    /** Busca a configuração inicial  */
    getConfig() {
      this.$initialSetting.getConfiguration().then((response) => {
        const initialConfig = response.data

        initialConfig.emailsResumoDiarioReservas =
          initialConfig.emailsResumoDiarioReservas ?? []

        if (initialConfig.gtmCliente) {
          this.updateOrAddGtm(initialConfig.gtmCliente, 'cash')
        }

        if (initialConfig.gtmCliente2) {
          this.updateOrAddGtm(initialConfig.gtmCliente2, 'cash')
        }

        if (initialConfig.gtmCliente3) {
          this.updateOrAddGtm(initialConfig.gtmCliente3, 'cash')
        }

        if (initialConfig.gtmCliente4) {
          this.updateOrAddGtm(initialConfig.gtmCliente4, 'cash')
        }

        if (initialConfig.gtmClientePontos) {
          this.updateOrAddGtm(initialConfig.gtmClientePontos, 'points')
        }

        if (initialConfig.gtmClientePontos2) {
          this.updateOrAddGtm(initialConfig.gtmClientePontos2, 'points')
        }

        if (initialConfig.gtmClientePontos3) {
          this.updateOrAddGtm(initialConfig.gtmClientePontos3, 'points')
        }

        if (initialConfig.gtmClientePontos4) {
          this.updateOrAddGtm(initialConfig.gtmClientePontos4, 'points')
        }

        this.configData.emailToken = initialConfig.emailToken

        initialConfig?.emailsResumoDiarioReservas[0] !== ''
          ? (this.configData.emailsResumoDiarioReservas =
              initialConfig.emailsResumoDiarioReservas)
          : null

        this.configData.cores = initialConfig.cores

        /** Salva as imagens */
        this.imgOpenGraph = initialConfig.openGraph
        this.imgLogo = initialConfig.logoHeaderHotel
        this.imgFavicon = initialConfig.favicon
        this.imgHeader = initialConfig.logoEmailDiario
        this.imgBanner = initialConfig.logoHeaderSecundarioEmails
        this.urlBanner = initialConfig.linkHeaderSecundarioEmails

        /** Informações de Tarifa Logada */
        if (initialConfig?.tarifaLogada) {
          this.activeLoggedRate = initialConfig.tarifaLogada?.ativo
          this.typeLoggedRate = initialConfig.tarifaLogada?.ativo
            ? initialConfig.tarifaLogada?.modelo
            : null
        }
      })
    },

    /** Busca os metodos  */
    getMethods() {
      this.$initialSetting.getMethods().then((response) => {
        this.arrayMethods = response.data
        this.mapMethodsFilter(this.arrayMethods)
      })
    },

    /** Cria o objeto de checkbox para os metodos  */
    mapMethodsFilter(arrMethods) {
      this.methodsOptions = arrMethods.map((metodo) => {
        return {
          label: metodo.metodo,
          value: metodo.codigo
        }
      })
    },

    /** Busca os hoteis  */
    getHotels() {
      this.$initialSetting.getHotels().then((response) => {
        this.arrayHotels = response.data
        this.mapHotelsFilter(this.arrayHotels)
      })
    },

    /** Cria o objeto de checkbox para os hoteis  */
    mapHotelsFilter(arrHotels) {
      this.hotelsOptions = arrHotels.map((hotel) => {
        return {
          label: hotel.nome,
          value: hotel.codigo
        }
      })
    },

    /** Busca os usuários  */
    getUsers() {
      this.$initialSetting.getUsers().then((response) => {
        this.arrayUsers = response.data
        this.gridData = this.gridData = this.standardizeCouponData(response)
        this.isLoadingTableContent = false
      })
    },

    standardizeCouponData(response) {
      return response.data.map((item) => {
        const obj = {
          data: item
        }

        return obj
      })
    },

    /** Cria um novo usuario */
    createNewUser() {
      this.$initialSetting.createUser().then(() => {
        this.showAlertChip = true
        this.getUsers()
      })
    },

    searchSelectedItemHandler(value) {
      this.$router.push({
        path: value.item.link
      })
    },

    /** Adicione e-mail */
    addEmail(e) {
      const hasLineWithMultipleEmails = e.target.value.includes(',')

      if (hasLineWithMultipleEmails) {
        const commaSeparatedEmails = e.target.value.split(',')

        commaSeparatedEmails.forEach((email) => {
          this.configData.emailsResumoDiarioReservas.push(email)
          e.target.value = ''
          this.fieldEmailsResumoDiarioReservas = ''
        })
      }

      if (e.target.value.trim() !== '') {
        if (e.target.value) {
          this.configData.emailsResumoDiarioReservas.push(e.target.value)
          e.target.value = ''
          this.fieldEmailsResumoDiarioReservas = ''
        } else {
          this.$bus.$emit('show-alert-chip', {
            message: 'O texto inserido não corresponde a um e-mail válido.'
          })
        }
      }
    },

    /** Remove um e-mail */
    removeEmail(index) {
      this.configData.emailsResumoDiarioReservas.splice(index, 1)
    },

    /** Retorna se um código do GTM é válido */
    validateGtmCode(code) {
      return code.search(/^GTM-[0-9A-Z]{4,8}$/gm) > -1
    },

    /** Divide e adiciona GTM ao array de GTMs em configuração inicial  */
    addGtm() {
      if (this.fieldGtm === '') return

      const hasLineWithMultipleGtm = this.fieldGtm.includes(',')

      if (hasLineWithMultipleGtm) {
        const commaSeparatedGtm = this.fieldGtm.split(',')

        commaSeparatedGtm.forEach((gtm) => {
          if (this.validateGtmCode(gtm)) {
            this.configData.gtm.push(gtm)
            this.fieldGtm = ''
            this.fieldGtm = ''
          } else {
            this.$bus.$emit('show-alert-chip', {
              message: `O texto ${gtm} não corresponde a um GTM válido.`
            })
          }
        })

        return
      }

      let gtm = this.fieldGtm
      if (this.validateGtmCode(gtm)) {
        this.configData.gtm.push(gtm)
        this.gtmDatatable.rows.push({
          data: {
            gtm: this.fieldGtm,
            actions: { cash: this.countCash < 4 }
          }
        })
        gtm = ''
        this.fieldGtm = ''
      } else {
        this.$bus.$emit('show-alert-chip', {
          message: 'O texto inserido não corresponde a um GTM válido.'
        })
      }
    },

    /** Remove os GTMs */
    removeGtm(rowData) {
      const index = this.gtmDatatable.rows.findIndex(
        (row) => row.data.gtm === rowData.gtm
      )
      if (index !== -1) {
        this.gtmDatatable.rows.splice(index, 1)
        this.configData.gtm.splice(index, 1)
      }
    },
    /** Abre o modal */
    openModalUser(userData) {
      this.selectedMethods = []
      this.selectedHotels = []
      this.userData = userData

      this.methodsOptions.map((option) => {
        if (userData.metodos.includes(option.value)) {
          this.selectedMethods.push(option)
        }
      })

      this.hotelsOptions.map((option) => {
        if (userData.hoteis.includes(option.value)) {
          this.selectedHotels.push(option)
        }
      })

      this.modalTable = true
      this.modalLogin = userData.login
      this.modalPassword = userData.senha
      this.modalDescription = userData.descricao
    },

    /** Atualiza o usuário */
    updateUser() {
      const dataUpdate = {
        codigo: this.userData.codigo,
        hoteis: this.selectedHotels.map((x) => x.value),
        metodos: this.selectedMethods.map((x) => x.value),
        descricao: this.modalDescription
      }

      this.$initialSetting.updateUser(dataUpdate).then(() => {
        this.modalTable = false
        this.showAlertChipUpdateUser = true
        this.getUsers()
      })

      this.modalDescription = ''
    },

    /* Atualiza o valor da descrição da politica de privacidade */
    updatePrivacyPolicy() {
      this.$initialSetting
        .updatePolicy({
          politica: this.description
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.response.data[0].message
          })
        })
    },

    /** Salva as configurações */
    async saveConfiguration() {
      /** Montando FormData para envio */
      const fieldsFD = new FormData()

      /** Informações de Tarifa Logada */
      fieldsFD.append('tarifaLogada.ativo', this.activeLoggedRate)
      fieldsFD.append(
        'tarifaLogada.modelo',
        this.activeLoggedRate ? this.typeLoggedRate : null
      )

      this.configData.emailsResumoDiarioReservas.forEach((item, index) => {
        fieldsFD.append(`emailsResumoDiarioReservas[${index}]`, item)
      })

      /** E-mail Token */
      fieldsFD.append('emailToken', this.configData.emailToken)

      /** Imagens */
      fieldsFD.append('logoHeaderEmails', this.imgLogo)
      fieldsFD.append('logoHeaderHotel', this.imgLogo)
      fieldsFD.append('logoEmailDiario', this.imgHeader)
      fieldsFD.append('logoHeaderSecundarioEmails', this.imgBanner)
      fieldsFD.append('linkHeaderSecundarioEmails', this.urlBanner)
      fieldsFD.append('favicon', this.imgFavicon)
      fieldsFD.append('openGraph', this.imgOpenGraph)

      /** Adiciona as cores ao form data */
      for (const key in this.configData.cores) {
        fieldsFD.append(`cores.${key}`, this.configData.cores[key])
      }

      /** GTM DINHEIRO */
      let pontosIndexCash = 0
      this.gtmDatatable.rows.forEach((item) => {
        if (item.data.actions.cash) {
          const key = `gtmCliente${pontosIndexCash ? pontosIndexCash + 1 : ''}`

          this.configData[key] = item.data.gtm
          fieldsFD.append(key, item.data.gtm)

          pontosIndexCash++
        }
      })

      /** GTM PONTOS */
      let pontosIndexPoints = 0
      this.gtmDatatable.rows.forEach((item) => {
        if (item.data.actions.points) {
          const key = `gtmClientePontos${
            pontosIndexPoints ? pontosIndexPoints + 1 : ''
          }`

          this.configData[key] = item.data.gtm
          fieldsFD.append(key, item.data.gtm)

          pontosIndexPoints++
        }
      })

      this.$initialSetting
        .createConfiguration(fieldsFD)
        .then(() => {
          this.updatePrivacyPolicy()
          this.$bus.$emit('show-alert-chip', {
            message: 'Configuração inicial salva com sucesso.'
          })
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.messages
          })
        })
    },

    /**
     * Abre a modal de deleção de um usuário
     * @param {object} userData: Dados do usuário que será removido
     */
    openDeleteUserModal(userData) {
      this.showDeleteUserModal = true
      this.userData = userData
    },

    /** Deleta um usuário */
    deleteUser() {
      const usuarios = this.userData.codigo

      this.$initialSetting
        .deleteUser(usuarios)
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Usuário excluído com sucesso.'
          })

          this.getUsers()
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.response.data[0].message
          })
        })

      this.userData = null
    }
  }
}
</script>
<style lang="scss" scoped>
.initial-setup {
  &-color-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 40px 24px;
  }
}
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
}

.side-bar-block {
  margin-bottom: 15px;
}
.sidebar {
  max-width: 215px;
  padding-top: 35px;
  margin-left: 15px;
  &--tip-title {
    margin-bottom: 10px;
    margin-top: 50px;
  }
  &--tip-text {
    line-height: 21px;
  }
  &--tip-alert {
    color: #e0414b;
  }
}
.application-colors {
  &--images {
    margin-bottom: 25px;
  }
}

.generic-spaces {
  margin-top: 10px;
  margin-bottom: 40px;

  &__small {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  &--email-input {
    margin-top: 25px;
  }

  &--image {
    margin-bottom: 20px;
  }
}

.modal-spaces {
  margin: 10px;

  &--inputs-margin {
    margin-bottom: 20px;
  }

  &__description {
    margin-bottom: 20px;
  }
}

.row-spaces {
  margin-top: 30px;

  &--input-code-color {
    margin-bottom: 50px;
  }
}
.input-color {
  margin-bottom: 0px;
}
.input {
  display: flex;
  position: relative;

  &--field {
    display: block;
    height: $input-height;
    padding: 1px 0;
    line-height: 21px;
    width: 100%;
    border: none;
    border-bottom: 1px solid $gray-darkest;
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-normal');

    &:focus {
      border-color: $blue-2;
      border-bottom-width: 2px;
    }
  }
}
.input-label {
  display: inline-flex;
  align-items: center;
  margin: 0;
  font-size: font-size('size-medium');
  font-weight: font-weight('weight-medium');
}
.form-input-label {
  margin-top: 25px;
  font-size: font-size('size-medium');
  font-weight: font-weight('weight-medium');
}
.form-input-button {
  margin-top: 22px;
  font-size: font-size('size-medium');
  font-weight: font-weight('weight-medium');
}
.character-count {
  float: right;
  margin-left: auto;
  padding-top: 7px;
  color: $gray-dark;
}
div.text-area-preview {
  height: 10em;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8e8e8;
  height: 205px;
  color: #7f7f7f;
}
div.text-area-preview p {
  margin: 0;
}

.text-area {
  padding-left: 10px;
  width: 750px;
  max-width: 750px;
}

.edit-colors-button {
  margin-top: 18px;
}
.margins-input-image {
  margin-top: 37px;
}
.email-chip {
  margin-top: 7px;
  margin-right: 7px;
  border-radius: 20px;
}
.last-buttons {
  margin-top: 34px;
}
.filters {
  padding: {
    bottom: 30px;
  }

  &--search_width {
    width: 214px;
  }

  .dropdowns-filters {
    margin-top: 16px;
  }
}
.datatable {
  max-height: initial;
  overflow: initial;

  &--action {
    border: none;
    background: inherit;
    color: pmw-colors('blue-100');

    &:hover {
      text-decoration: underline;
    }

    &__delete {
      &:hover {
        text-decoration: underline;
      }
      border: none;
      background: inherit;
      color: pmw-colors('red-100');
    }
  }

  ::v-deep {
    .thead {
      top: 50px;

      &:before,
      &:after {
        content: '';
        width: 50px;
        height: 100%;
        position: absolute;
        top: 0;
        background-color: $white;
        right: 100%;
      }

      &::after {
        right: initial;
        left: 100%;
      }
    }

    .dropdown {
      &-item {
        &:hover,
        &:active {
          background-color: $gray-lightest;
        }
      }
      .danger-color {
        &.dropdown-item {
          color: $red-2;
        }
      }

      &--trigger-button {
        color: $gray-mid;
      }

      &--options {
        min-width: 100px;
      }
    }

    &--quantity-column__danger-color {
      color: $red-2;
    }
  }
}
.banner-action {
  margin-right: 146px;
  width: 360px;
  ::v-deep .pmw-input--field {
    background-color: $pmw-white;
  }

  ::v-deep .pmw-form-field--feedback {
    position: static;
    color: $pmw-red-2;
    margin-top: 5px;
  }

  ::v-deep .pmw-input--clear {
    position: static;
    margin-top: 5px;
  }
}
.pmw-home {
  display: flex;
  margin: 0 auto;

  &--header {
    display: flex;
    margin: 30px auto;
  }
}
.pmw-input-file {
  margin-right: 12px;

  &--header {
    max-width: 600px;
  }
}
::v-deep .pmw-image-upload .pmw-box-file {
  margin: 15px auto 15px auto;
}
.pmw-page-actions {
  padding-top: 60px;
}

.gtm-datatable {
  margin-top: 15px;
  min-width: 350px;
  &--gtm-slot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
  }

  &--gtm-action {
    display: flex;
    align-items: center;
    .gtm-text {
      margin: 0px 20px 0px 10px;
    }
  }

  &--gtm-text {
    color: $gray-darkest;
  }

  &--action {
    width: 9px;
    height: 9px;
    color: $black-1;
    cursor: pointer;
  }
}

.logged-rate {
  &--title {
    margin-bottom: 24px;
  }

  &--description {
    color: pmw-colors(grey-90);
    margin-bottom: 24px;
    line-height: 21px;
  }

  &--link {
    color: pmw-colors('blue-100');
    text-decoration: underline;
  }

  &--activate-incentive {
    color: pmw-colors(grey-90);
    align-items: center;
    display: flex;
    gap: 8px;
  }

  &--modal-login {
    width: 337px;
  }

  &--options {
    margin-top: 32px;

    &-label {
      margin-bottom: 24px;

      &__alert {
        color: pmw-colors(red-100);
      }
    }

    &-grid {
      display: flex;
      gap: 40px;
      align-items: flex-start;
    }
  }

  &-option {
    &--radio {
      margin-bottom: 21px;
    }

    &--image {
      width: 337px;
    }
  }
}

.pmw-input-radio ::v-deep {
  input:checked ~ {
    .pmw-input-radio--fake-radio {
      border-color: $pmw-blue;

      &:before {
        background-color: $pmw-blue;
      }
    }
  }
}

.pmw-input-checkbox ::v-deep {
  .pmw-input-checkbox--label__overflow {
    overflow: initial;
  }

  input:checked ~ {
    .pmw-input-checkbox--fakebox {
      border-color: $pmw-blue;
      background-color: $pmw-blue;

      &:before {
        background-color: $pmw-blue;
      }
    }
  }
}
</style>
